/**
 * One pivot in full: what Fable read, the facts it rests on, and the members
 * it is made of — each a link back to the map. The same errand v1's
 * PivotPointDetailModal runs, rebuilt on v2's candidate shape (never shared:
 * CLAUDE.md, Redesigns).
 */
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { X, Check, RotateCcw, ExternalLink } from 'lucide-react';
import { DIRECTION, SOURCE, LANE, directionSource } from './PivotCard';

const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 24px;`;
const Panel = styled.div`
  width: min(760px, 100%); max-height: calc(100vh - 48px); overflow: auto; background: #14141f; border: 1px solid #2f2f40; border-radius: 16px;
  color: #e8e8ef; padding: 22px 24px 24px; display: flex; flex-direction: column; gap: 14px;
`;
const Top = styled.div`display: flex; align-items: center; gap: 10px;`;
const Dir = styled.span`font-family: ui-monospace, monospace; font-size: 0.66rem; letter-spacing: 0.1em; padding: 3px 9px; border-radius: 5px; color: ${(p) => p.$c}; background: ${(p) => p.$bg};`;
const Close = styled.button`margin-left: auto; background: transparent; border: 1px solid rgba(255,255,255,0.14); color: #ddd; border-radius: 8px; padding: 6px; cursor: pointer; display: inline-flex;`;
const H = styled.h2`margin: 0; font-size: 1.35rem; line-height: 1.25; font-weight: 600;`;
const P = styled.p`margin: 0; color: #c9cbd6; line-height: 1.55;`;
const Facts = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px;`;
const Fact = styled.div`background: #1a1a26; border: 1px solid #2f2f40; border-radius: 10px; padding: 10px 12px; .n { font-size: 1.4rem; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1; } .l { font-size: 0.7rem; color: #9ca3af; letter-spacing: 0.06em; text-transform: uppercase; margin-top: 4px; }`;
const Sect = styled.h3`margin: 6px 0 0; font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase; color: #9ca3af;`;
const Members = styled.ul`list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px;`;
const Member = styled.li`display: grid; grid-template-columns: 8px 1fr auto; gap: 10px; align-items: start; font-size: 0.88rem; color: #d6d8e2;
  .v { width: 8px; height: 8px; border-radius: 50%; margin-top: 7px; background: ${(p) => p.$c}; }
  a { color: #c4b5fd; text-decoration: none; } a:hover { color: #e9d5ff; }
  .s { font-size: 0.72rem; color: #9ca3af; white-space: nowrap; padding-top: 3px; }`;
const Actions = styled.div`display: flex; gap: 6px; margin-top: 4px; flex-wrap: wrap;`;
const Btn = styled.button`display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.04); color: #ddd; font: inherit; font-size: 0.84rem; cursor: pointer; &:hover { background: rgba(255,255,255,0.09); } &.accept { border-color: rgba(167,139,250,0.6); color: #e9d5ff; }`;
const MapLink = styled.a`display: inline-flex; align-items: center; gap: 6px; color: #c4b5fd; font-size: 0.84rem; text-decoration: none; margin-left: auto; &:hover { color: #e9d5ff; }`;

const VAL = { wanted: '#3ecf8e', unwanted: '#e0708a', unknown: '#4b4f63' };

export default function PivotDetailModal({ c, members = [], scenarioName, mapHref, onStatus, canDecide = false, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!c) return null;
  const d = DIRECTION[c.direction] || DIRECTION.contested;
  const conv = c.convergence || {};
  const f = c.leverage?.facts || {};
  const loops = (f.reinforcingLoops || 0) + (f.balancingLoops || 0);
  return (
    <Overlay onClick={onClose} role="dialog" aria-modal="true" aria-label={c.title}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <Top>
          {LANE[c.lane] && <Dir $c={LANE[c.lane].color} $bg={LANE[c.lane].bg} title={LANE[c.lane].hint}>{LANE[c.lane].label}</Dir>}
          <Dir $c={d.color} $bg={d.bg}>{d.label}</Dir>
          <span style={{ fontSize: '0.72rem', color: '#8a8fa3' }}>{SOURCE[directionSource(c)]}</span>
          <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.72rem', color: '#8a8fa3' }}>#{c.rank}</span>
          <Close type="button" onClick={onClose} aria-label="Sulje"><X size={16} /></Close>
        </Top>
        <H>{c.title}</H>
        {c.description && <P>{c.description}</P>}
        <Facts>
          <Fact><div className="n">{conv.participants ?? '–'}</div><div className="l">osallistujaa · {conv.scenarios} skenaariota</div></Fact>
          <Fact><div className="n">{f.descendants ?? 0}</div><div className="l">seurausta alla</div></Fact>
          <Fact><div className="n">{loops}</div><div className="l">{f.reinforcingLoops ? `${f.reinforcingLoops} vahvistava` : ''}{f.reinforcingLoops && f.balancingLoops ? ' · ' : ''}{f.balancingLoops ? `${f.balancingLoops} tasapainottava` : ''}{loops ? '' : 'ei kierteitä merkitty'}</div></Fact>
          <Fact><div className="n">{(f.choicePoint ? 1 : 0) + (f.crossLinks || 0) + (f.blockers || 0)}</div><div className="l">{[f.choicePoint ? 'valintakohta' : null, f.crossLinks ? `${f.crossLinks} ristiinyhteys` : null, f.blockers ? `${f.blockers} este` : null].filter(Boolean).join(' · ') || 'ei rakenteellista vipua'}</div></Fact>
        </Facts>
        <Sect>Mistä tämä koostuu</Sect>
        <Members>
          {members.map((m) => (
            <Member key={m.id} $c={VAL[m.valence] || VAL.unknown}>
              <span className="v" />
              <a href={mapHref([m.id])} title="Näytä kartalla">{m.text}</a>
              <span className="s">{scenarioName?.(m.scenarioId) || ''}</span>
            </Member>
          ))}
          {!members.length && <li style={{ color: '#9ca3af', fontSize: '0.84rem' }}>Jäseniä ei voitu lukea kartalta.</li>}
        </Members>
        <Actions>
          {canDecide && c.status !== 'accepted' && <Btn className="accept" type="button" onClick={() => onStatus?.(c, 'accepted')}><Check size={14} /> Hyväksy</Btn>}
          {canDecide && c.status !== 'dismissed' && <Btn type="button" onClick={() => onStatus?.(c, 'dismissed')}><X size={14} /> Hylkää</Btn>}
          {canDecide && c.status !== 'proposed' && <Btn type="button" onClick={() => onStatus?.(c, 'proposed')}><RotateCcw size={14} /> Palauta</Btn>}
          <MapLink href={mapHref(c.memberIds || [])}><ExternalLink size={14} /> Näytä kaikki kartalla</MapLink>
        </Actions>
      </Panel>
    </Overlay>
  );
}
